import type { OnboardingData } from "../use-onboarding";

/** One Q&A pair in the editor. `sourceId` is set once it exists as a Knowledge Base source. */
export interface QaDraft {
  id: string;
  question: string;
  answer: string;
  sourceId?: string | undefined;
}

type SavedQa = NonNullable<OnboardingData["qa"]>[number];

let sequence = 0;

export function blankDraft(): QaDraft {
  sequence += 1;
  return { id: `qa-${String(sequence)}`, question: "", answer: "" };
}

/** Keeps at least one unsaved row at the end of the list to type in. */
export function withBlankRow(drafts: readonly QaDraft[]): QaDraft[] {
  return drafts.some((draft) => draft.sourceId === undefined) ? [...drafts] : [...drafts, blankDraft()];
}

/** Wizard progress → editor rows. */
export function draftsFromProgress(saved: OnboardingData["qa"]): QaDraft[] {
  return withBlankRow((saved ?? []).map<QaDraft>((pair) => ({ ...blankDraft(), question: pair.question, answer: pair.answer, sourceId: pair.source_id })));
}

/** Editor rows → the progress JSON kept for resume (blank rows are not stored). */
export function progressFromDrafts(drafts: readonly QaDraft[]): SavedQa[] {
  return drafts
    .filter((draft) => draft.question.trim() && draft.answer.trim())
    .map((draft) => ({ question: draft.question.trim(), answer: draft.answer.trim(), ...(draft.sourceId ? { source_id: draft.sourceId } : {}) }));
}

export interface QaReview {
  /** Complete rows that are not sources yet. */
  pending: QaDraft[];
  /** Rows with only a question or only an answer. */
  incomplete: QaDraft[];
}

export function reviewDrafts(drafts: readonly QaDraft[]): QaReview {
  const open = drafts.filter((draft) => draft.sourceId === undefined);
  const hasQuestion = (draft: QaDraft) => draft.question.trim().length > 0;
  const hasAnswer = (draft: QaDraft) => draft.answer.trim().length > 0;
  return {
    pending: open.filter((draft) => hasQuestion(draft) && hasAnswer(draft)),
    incomplete: open.filter((draft) => hasQuestion(draft) !== hasAnswer(draft)),
  };
}
